import { type FC, type RefObject, useMemo, useState } from 'react';

import { HotTable, type HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type {
  DosingMatrixRow,
  DosingMatrixRowKey,
  TreatmentOption,
} from '@/components/Samples/Dosing/DosingPlan.utils';
import { _notNil } from '@/littledash';
import { registerHOTModules } from '@/support/hot';
import type { CellChange, ChangeSource } from '@/support/hot.types';

import {
  buildColumns,
  buildTreatmentCellSettings,
  type DosingSessionGridRow,
  initDosingSessionSettings,
  TREATMENT_COLUMN_INDEX,
  type TreatmentCellSettings,
} from './DosingSession.utils';

registerHOTModules();

interface DosingSessionTableProps {
  rows: Array<DosingMatrixRow>;
  gridRows: Array<DosingSessionGridRow>;
  /** Resolves the treatments on offer for a matrix row, keyed by the study group in column 1. */
  getTreatmentOptions: (rowKey: DosingMatrixRowKey) => Array<TreatmentOption>;
  /** Heads the treatment column, matching how the matrix labels each session's column. */
  sessionName: string;
  onGridChange?: () => void;
  innerRef: RefObject<HotTableRef | null>;
  testId?: string;
}

/**
 * Grid a designer uses to assign one treatment to each study group within a single dosing session.
 * The study group and animal count are read-only context; only the treatment column is editable,
 * and each row offers just the treatments mapped to its own group.
 */
const DosingSessionTable: FC<DosingSessionTableProps> = ({
  rows,
  gridRows,
  getTreatmentOptions,
  sessionName,
  onGridChange,
  innerRef,
  testId = 'dosing-session__hot',
}) => {
  const [initialData] = useState<Array<DosingSessionGridRow>>(() => gridRows.map((row) => ({ ...row })));
  const columns = useMemo(() => buildColumns(), []);

  const treatmentCells = useMemo<Array<TreatmentCellSettings>>(
    () => rows.map((row) => buildTreatmentCellSettings(getTreatmentOptions(row.key))),
    [rows, getTreatmentOptions]
  );

  const settings = useMemo(
    () => ({
      ...initDosingSessionSettings(columns, rows.length, sessionName),
      cells: (row: number, col: number) => (col === TREATMENT_COLUMN_INDEX ? (treatmentCells[row] ?? {}) : {}),
      afterChange: (changes: Array<CellChange | null> | null, source: ChangeSource) => {
        if (source !== 'loadData' && _notNil(changes)) {
          onGridChange?.();
        }
      },
    }),
    [columns, rows.length, sessionName, treatmentCells, onGridChange]
  );

  return (
    <div className="w-100 overflow-auto ow-spreadsheet-styles">
      <div className="hotSpreadSheet spreadSheet" data-testid={testId}>
        <div className="ui-card spreedSheet">
          <HotTable
            ref={innerRef as RefObject<HotTableRef>}
            data={initialData}
            licenseKey={AppConfig.handsOnTableLicence}
            headerClassName="custom-header"
            testId={`${testId}-table`}
            {...settings}
          />
        </div>
      </div>
    </div>
  );
};

export default DosingSessionTable;
